import { createFileRoute, useNavigate, Link, Outlet, useLocation } from "@tanstack/react-router";
import { useAuth } from "@/hooks/useAuth";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ShieldCheck, Users, Stethoscope, CreditCard, Mail, Crown, BarChart3, Loader2, Trash2, ExternalLink, Star, Search, Sparkles, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { StickyPageHeader, PageBody } from "@/components/dashboard/DashboardShell";
import {
 loadOverviewFn,
 approveClaimFn,
 rejectClaimFn,
 loadListingsFn,
 updateListingsFn,
 deleteListingsFn,
} from "./-dashboard.admin.functions";

export const Route = createFileRoute("/dashboard/admin")({
 component: AdminDashboard,
});

interface ClaimRow {
 id: string; provider_id: string; user_id: string; status: string; created_at: string;
 contact_email: string | null; contact_phone: string | null;
 providers?: { name: string; practice: string } | null;
}
interface RoleRow { user_id: string; role: string }

function AdminDashboard() {
 const { user, loading, primaryRole } = useAuth();
 const navigate = useNavigate();
 const location = useLocation();
 const isIndex = location.pathname === "/dashboard/admin" || location.pathname === "/dashboard/admin/";
 const [claims, setClaims] = useState<ClaimRow[]>([]);
 const [roles, setRoles] = useState<RoleRow[]>([]);
 const [tab, setTab] = useState<"overview" | "listings" | "claims" | "appointments" | "users" | "subs">("overview");
 const [listingsFilter, setListingsFilter] = useState<StatusFilter>("all");
 const [appointments, setAppointments] = useState<any[]>([]);
 const [counts, setCounts] = useState<{ total: number; claimed: number; premium: number; featured: number }>({ total: 0, claimed: 0, premium: 0, featured: 0 });
 const goListings = (f: StatusFilter) => { setListingsFilter(f); setTab("listings"); if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" }); };

   const reload = async () => {
   // Bounded loads — never pull entire tables. Tabs do their own paginated fetches.
   const { claims: cs, roles: rs, appointments: appts, stats: s } = await loadOverviewFn();
   // Reshape joined provider columns back into the nested shape the UI expects.
   setClaims((cs ?? []).map((c: any) => ({
   ...c,
   providers: c.provider_name != null || c.provider_practice != null
     ? { name: c.provider_name, practice: c.provider_practice }
     : null,
   })) as any);
   setRoles((rs ?? []) as any);
   setAppointments((appts ?? []).map((a: any) => ({
   ...a,
   providers: a.provider_name != null || a.provider_practice != null || a.provider_city != null
     ? { name: a.provider_name, practice: a.provider_practice, city: a.provider_city }
     : null,
   })));
   setCounts({
   total: Number(s?.total ?? 0),
   claimed: Number(s?.claimed ?? 0),
   premium: Number(s?.premium ?? 0),
   featured: Number(s?.featured ?? 0),
   });
   };


 useEffect(() => {
 if (loading) return;
 if (!user) { navigate({ to: "/login" }); return; }
 if (primaryRole && primaryRole !== "admin") { navigate({ to: "/dashboard" }); return; }
 if (primaryRole === "admin" && isIndex) reload();
 }, [user, loading, primaryRole, isIndex]);

 if (loading || !user || primaryRole !== "admin") return <FullLoader />;

 // Render child admin routes (add-listings, listings/$id, etc.) instead of the index dashboard
 if (!isIndex) return <Outlet />;

 const approveClaim = async (claim: ClaimRow) => {
 try {
 await approveClaimFn({ data: { claimId: claim.id, providerId: claim.provider_id, userId: claim.user_id } });
 toast.success("Claim approved"); reload();
 } catch {
 toast.error("Failed to approve");
 }
 };
 const rejectClaim = async (claim: ClaimRow) => {
 await rejectClaimFn({ data: { claimId: claim.id } });
 toast.success("Claim rejected"); reload();
 };

 const patients = roles.filter((r) => r.role === "patient").length;
 const doctors = roles.filter((r) => r.role === "doctor").length;
 const claimed = counts.claimed;
 const premium = counts.premium;
 const pending = claims.filter((c) => c.status === "pending").length;

  const tabsRow = (
    <div className="flex flex-wrap gap-0 -mb-px">
      <Tab active={tab === "overview"} onClick={() => setTab("overview")} icon={<BarChart3 className="h-3.5 w-3.5" />}>Overview</Tab>
      <Tab active={tab === "listings"} onClick={() => setTab("listings")} icon={<Stethoscope className="h-3.5 w-3.5" />}>Listings</Tab>
      <Tab active={tab === "claims"} onClick={() => setTab("claims")} icon={<ShieldCheck className="h-3.5 w-3.5" />}>Claims</Tab>
      <Tab active={tab === "appointments"} onClick={() => setTab("appointments")} icon={<CalendarClock className="h-3.5 w-3.5" />}>Appointments</Tab>
      <Tab active={tab === "users"} onClick={() => setTab("users")} icon={<Users className="h-3.5 w-3.5" />}>Users</Tab>
      <Tab active={tab === "subs"} onClick={() => setTab("subs")} icon={<CreditCard className="h-3.5 w-3.5" />}>Revenue</Tab>
    </div>
  );

  return (
 <>
 <StickyPageHeader
   icon={<Sparkles className="h-4 w-4 text-primary" />}
   title="Platform overview"
   subtitle="Operational metrics, listings, claims, and revenue at a glance."
   tabs={tabsRow}
 />
 <PageBody className="space-y-6">
 {tab === "overview" && (
 <>
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-7 gap-3">
   <Metric tone="blue" label="Listings" value={counts.total.toLocaleString()} icon={<Stethoscope className="h-4 w-4" />} sub="Total providers" onClick={() => goListings("all")} />
   <Metric tone="sky" label="Claimed" value={claimed} icon={<ShieldCheck className="h-4 w-4" />} sub={`${counts.total ? Math.round((claimed / counts.total) * 100) : 0}% of total`} onClick={() => goListings("claimed")} />
   <Metric tone="violet" label="Premium" value={premium} icon={<Crown className="h-4 w-4" />} sub="Paid plans" onClick={() => goListings("premium")} />
   <Metric tone="amber" label="Pending claims" value={pending} icon={<Mail className="h-4 w-4" />} sub="Awaiting review" onClick={() => { setTab("claims"); window.scrollTo({ top: 0, behavior: "smooth" }); }} />
   <Metric tone="emerald" label="Patients" value={patients} icon={<Users className="h-4 w-4" />} sub="Active accounts" onClick={() => setTab("users")} />
   <Metric tone="teal" label="Providers" value={doctors} icon={<Stethoscope className="h-4 w-4" />} sub="Doctor accounts" onClick={() => setTab("users")} />
   <Metric tone="rose" label="MRR (est.)" value={`$${premium * 29}`} icon={<CreditCard className="h-4 w-4" />} sub="Monthly revenue" onClick={() => setTab("subs")} />
  </div>
  <div>
   <h2 className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground/80 font-semibold mb-3 font-display">Quick actions</h2>
   <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
    <QuickTile to="/dashboard/admin/notifications" icon={<Mail className="h-4 w-4" />} label="Notifications" />
    <QuickTile to="/dashboard/admin/add-listings" icon={<Stethoscope className="h-4 w-4" />} label="Add listings" search={{ tab: "single" }} />
    <QuickTile to="/dashboard/admin/avatars" icon={<Users className="h-4 w-4" />} label="Avatars" />
    <QuickTile to="/dashboard/admin/bulk-imports" icon={<Sparkles className="h-4 w-4" />} label="Bulk imports" />
    <QuickTile to="/dashboard/admin/reviews" icon={<Star className="h-4 w-4" />} label="Reviews" />
    <QuickTile to="/dashboard/admin/requests" icon={<ShieldCheck className="h-4 w-4" />} label="Update requests" />
   </div>
  </div>
 </>
 )}


 {tab === "listings" && (
 <ListingsSection initialStatus={listingsFilter} key={listingsFilter} />
 )}

 {tab === "claims" && (
 <Section title="Claim requests" icon={<ShieldCheck className="h-4 w-4" />}>
 <div className="card-surface-flat divide-y divide-border">
 {claims.map((c) => (
 <div key={c.id} className="p-4 flex flex-wrap items-center justify-between gap-3">
 <div>
 <p className="font-medium text-sm">{c.providers?.practice ?? "Listing"}</p>
 <p className="text-xs text-muted-foreground">{c.contact_email ?? " "} · {new Date(c.created_at).toLocaleDateString()}</p>
 </div>
 <div className="flex items-center gap-2">
 <span className={`text-xs px-2 py-0.5 rounded-md border ${c.status === "approved" ? "border-primary text-primary" : c.status === "rejected" ? "border-destructive text-destructive" : "border-border text-muted-foreground"}`}>{c.status}</span>
 {c.status === "pending" && (
 <>
 <Button size="sm" variant="outline" onClick={() => rejectClaim(c)}>Reject</Button>
 <Button size="sm" onClick={() => approveClaim(c)}>Approve</Button>
 </>
 )}
 </div>
 </div>
 ))}
 {claims.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">No claim requests yet.</div>}
 </div>
 </Section>
 )}

 {tab === "appointments" && (
 <Section title="All appointments" icon={<CalendarClock className="h-4 w-4" />}>
 {appointments.length === 0 ? (
 <div className="card-surface-flat p-10 text-center text-sm text-muted-foreground">No appointments yet.</div>
 ) : (
 <div className="card-surface-flat divide-y divide-border">
 {appointments.map((a) => {
 const when = new Date(a.appointment_at);
 return (
 <div key={a.id} className="px-4 py-3 flex flex-wrap justify-between items-center gap-2">
 <div className="min-w-0">
 <p className="font-medium text-sm truncate">
 {a.providers?.practice ?? a.providers?.name ?? "Provider"} · {a.customer_name}
 </p>
 <p className="text-xs text-muted-foreground truncate">
 {when.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
 {a.service_requested ? ` · ${a.service_requested}` : ""} · {a.customer_email}
 </p>
 </div>
 <span className={`text-xs px-2 py-1 rounded-full font-medium ${
 a.status === "confirmed" ? "bg-green-100 text-green-800" :
 a.status === "canceled" ? "bg-red-100 text-red-800" :
 "bg-blue-100 text-blue-800"
 }`}>{a.status}</span>
 </div>
 );
 })}
 </div>
 )}
 </Section>
 )}

 {tab === "users" && (
 <Section title="Users by role" icon={<Users className="h-4 w-4" />}>
 <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
 <RoleCard label="Patients" count={patients} />
 <RoleCard label="Doctors / Practices" count={doctors} />
 <RoleCard label="Admins" count={roles.filter((r) => r.role === "admin").length} />
 </div>
 <p className="text-xs text-muted-foreground mt-4">Detailed user search requires elevated access, coming with the next admin tools release.</p>
 </Section>
 )}

 {tab === "subs" && (
 <Section title="Subscriptions & revenue" icon={<CreditCard className="h-4 w-4" />}>
 <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
 <Metric label="Premium subscribers" value={premium} icon={<Crown className="h-4 w-4" />} />
 <Metric label="Estimated MRR" value={`$${premium * 29}`} icon={<CreditCard className="h-4 w-4" />} />
 <Metric label="Estimated ARR" value={`$${premium * 29 * 12}`} icon={<CreditCard className="h-4 w-4" />} />
 </div>
 <p className="text-xs text-muted-foreground mt-4">Connect Stripe to unlock real-time revenue tracking and subscription management.</p>
 </Section>
 )}
 </PageBody>
 </>
 );
}

function Tab({ active, onClick, icon, children }: any) {
 return <button onClick={onClick} className={`flex items-center gap-1.5 px-3 py-2 text-xs sm:text-sm font-medium border-b-2 -mb-px transition ${active ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>{icon}{children}</button>;
}
function Section({ title, icon, children }: any) {
 return <section><h2 className="flex items-center gap-2 text-base font-semibold mb-3 font-display">{icon}{title}</h2>{children}</section>;
}

type Tone = "blue" | "sky" | "violet" | "amber" | "emerald" | "teal" | "rose";
const TONE_STYLES: Record<Tone, { bg: string; ring: string; chip: string; value: string }> = {
 blue:    { bg: "bg-[oklch(0.97_0.025_250)]", ring: "ring-[oklch(0.88_0.05_250)]/60", chip: "bg-primary/15 text-primary", value: "text-primary" },
 sky:     { bg: "bg-[oklch(0.97_0.03_230)]",  ring: "ring-[oklch(0.88_0.06_230)]/60", chip: "bg-[oklch(0.92_0.08_230)] text-[oklch(0.45_0.13_230)]", value: "text-[oklch(0.45_0.13_230)]" },
 violet:  { bg: "bg-[oklch(0.97_0.03_300)]",  ring: "ring-[oklch(0.88_0.06_300)]/60", chip: "bg-[oklch(0.92_0.08_300)] text-[oklch(0.45_0.16_300)]", value: "text-[oklch(0.45_0.16_300)]" },
 amber:   { bg: "bg-[oklch(0.97_0.04_85)]",   ring: "ring-[oklch(0.88_0.08_85)]/60",  chip: "bg-[oklch(0.92_0.1_85)] text-[oklch(0.5_0.15_60)]",    value: "text-[oklch(0.5_0.15_60)]" },
 emerald: { bg: "bg-[oklch(0.97_0.03_160)]",  ring: "ring-[oklch(0.88_0.06_160)]/60", chip: "bg-[oklch(0.92_0.08_160)] text-[oklch(0.45_0.13_160)]", value: "text-[oklch(0.45_0.13_160)]" },
 teal:    { bg: "bg-[oklch(0.97_0.03_195)]",  ring: "ring-[oklch(0.88_0.06_195)]/60", chip: "bg-[oklch(0.92_0.08_195)] text-[oklch(0.45_0.13_195)]", value: "text-[oklch(0.45_0.13_195)]" },
 rose:    { bg: "bg-[oklch(0.97_0.03_20)]",   ring: "ring-[oklch(0.88_0.07_20)]/60",  chip: "bg-[oklch(0.92_0.09_20)] text-[oklch(0.5_0.17_20)]",   value: "text-[oklch(0.5_0.17_20)]" },
};

function Metric({ label, value, icon, sub, tone = "blue", onClick }: { label: string; value: string | number; icon: React.ReactNode; sub?: string; tone?: Tone; onClick?: () => void }) {
 const t = TONE_STYLES[tone];
 const cls = `rounded-md border border-border ${t.bg} p-4 transition text-left w-full ${onClick ? "cursor-pointer hover:border-primary/40" : ""}`;
 const content = (
  <>
   <div className="flex items-center gap-2 mb-3">
    <span className={`inline-flex items-center justify-center h-7 w-7 rounded-md ${t.chip}`}>{icon}</span>
    <span className="text-[11px] uppercase tracking-wider text-foreground/60 font-semibold truncate">{label}</span>
   </div>
   <p className={`text-[28px] leading-none font-bold tabular-nums ${t.value}`}>{value}</p>
   {sub && <p className="text-[11px] text-muted-foreground mt-2 truncate">{sub}</p>}
  </>
 );
 if (onClick) return <button type="button" onClick={onClick} className={cls}>{content}</button>;
 return <div className={cls}>{content}</div>;
}

function QuickTile({ to, icon, label, search }: { to: string; icon: React.ReactNode; label: string; search?: any }) {
 return (
  <Link to={to as any} search={search as any} className="group hover-lift rounded-md border border-border bg-card px-4 py-3.5 hover:border-primary/40 hover:bg-primary/[0.03] transition flex items-center gap-3 text-sm">
   <span className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-muted text-muted-foreground group-hover:bg-primary/15 group-hover:text-primary shrink-0 transition-colors">{icon}</span>
   <span className="truncate font-medium text-foreground/85">{label}</span>
   <ExternalLink className="h-3.5 w-3.5 opacity-30 ml-auto shrink-0 group-hover:opacity-60" />
  </Link>
 );
}


function RoleCard({ label, count }: { label: string; count: number }) {
 return <div className="card-surface-flat p-6"><p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">{label}</p><p className="text-4xl font-semibold mt-3 text-foreground">{count}</p></div>;
}
function FullLoader() {
 return <div className="min-h-screen flex items-center justify-center text-muted-foreground gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
}

const STATUS_OPTIONS = ["all", "unclaimed", "claimed_free", "claimed", "premium", "featured"] as const;
type StatusFilter = typeof STATUS_OPTIONS[number];

type ListingRow = {
 id: string;
 practice: string;
 name: string;
 specialty: string;
 city: string;
 state: string | null;
 email: string | null;
 listing_status: string;
 is_featured: boolean;
 is_active: boolean;
 views: number;
};

const PAGE_SIZE = 50;

function ListingsSection({ initialStatus = "all" }: { initialStatus?: StatusFilter }) {
 const [q, setQ] = useState("");
 const [debouncedQ, setDebouncedQ] = useState("");
 const [status, setStatus] = useState<StatusFilter>(initialStatus);
 const [page, setPage] = useState(0);
 const [rows, setRows] = useState<ListingRow[]>([]);
 const [hasMore, setHasMore] = useState(false);
 const [loading, setLoading] = useState(false);
 const [selected, setSelected] = useState<Set<string>>(new Set());
 const [busy, setBusy] = useState(false);

 useEffect(() => {
 const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
 return () => clearTimeout(t);
 }, [q]);

 useEffect(() => { setPage(0); }, [debouncedQ, status]);

 const load = async () => {
 setLoading(true);
 // Slim select: only columns rendered in the row. No COUNT — counting 6M rows times out.
 // Cursor-style pagination: fetch PAGE_SIZE+1 to detect a next page without a COUNT.
 try {
 const data = await loadListingsFn({ data: { status, search: debouncedQ, page, pageSize: PAGE_SIZE } });
 const fetched = (data ?? []) as ListingRow[];
 setHasMore(fetched.length > PAGE_SIZE);
 setRows(fetched.slice(0, PAGE_SIZE));
 } catch (e: any) {
 toast.error(e?.message ?? "Failed to load");
 } finally {
 setLoading(false);
 }
 };


 useEffect(() => { load(); /* eslint-disable-next-line */ }, [debouncedQ, status, page]);

 const allSelected = rows.length > 0 && rows.every((p) => selected.has(p.id));
 const toggleAll = () => {
 const next = new Set(selected);
 if (allSelected) rows.forEach((p) => next.delete(p.id));
 else rows.forEach((p) => next.add(p.id));
 setSelected(next);
 };
 const toggleOne = (id: string) => {
 const next = new Set(selected);
 if (next.has(id)) next.delete(id); else next.add(id);
 setSelected(next);
 };

 const ids = () => Array.from(selected);

 const update = async (patch: Record<string, any>, label: string) => {
 if (selected.size === 0) { toast.error("Select at least one listing"); return; }
 setBusy(true);
 try {
 await updateListingsFn({ data: { ids: ids(), patch } });
 toast.success(`${label} applied to ${selected.size}`); setSelected(new Set()); load();
 } catch (e: any) {
 toast.error(e?.message ?? "Failed to update");
 } finally {
 setBusy(false);
 }
 };

 const setPremiumBulk = () => update({ listing_status: "premium", plan: "premium_monthly" }, "Premium");

 const togglePremium = async (p: ListingRow) => {
 const isPremium = p.listing_status === "premium";
 try {
 await updateListingsFn({ data: {
 ids: [p.id],
 patch: isPremium ? { listing_status: "claimed", plan: "free" } : { listing_status: "premium", plan: "premium_monthly" },
 } });
 toast.success(isPremium ? "Premium removed" : "Premium enabled"); load();
 } catch (e: any) { toast.error(e?.message ?? "Failed to update"); }
 };
 const toggleFeatured = async (p: ListingRow) => {
 try {
 await updateListingsFn({ data: { ids: [p.id], patch: { is_featured: !p.is_featured } } });
 toast.success(p.is_featured ? "Unfeatured" : "Featured"); load();
 } catch (e: any) { toast.error(e?.message ?? "Failed to update"); }
 };
 const deleteOne = async (id: string) => {
 if (!confirm("Delete this listing? This cannot be undone.")) return;
 try {
 await deleteListingsFn({ data: { ids: [id] } });
 toast.success("Listing deleted"); load();
 } catch (e: any) { toast.error(e?.message ?? "Failed to delete"); }
 };
 const bulkDelete = async () => {
 if (selected.size === 0) return;
 if (!confirm(`Delete ${selected.size} listings? This cannot be undone.`)) return;
 setBusy(true);
 try {
 await deleteListingsFn({ data: { ids: ids() } });
 toast.success(`${selected.size} deleted`); setSelected(new Set()); load();
 } catch (e: any) {
 toast.error(e?.message ?? "Failed to delete");
 } finally {
 setBusy(false);
 }
 };

 const start = rows.length === 0 ? 0 : page * PAGE_SIZE + 1;
 const end = page * PAGE_SIZE + rows.length;

 return (
 <section>
 <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
 <h2 className="flex items-center gap-2 text-lg font-semibold font-display">
 <Stethoscope className="h-4 w-4" /> All listings{" "}
 <span className="text-xs font-normal text-muted-foreground">
 {rows.length > 0 ? `(showing ${start.toLocaleString()}${end.toLocaleString()})` : ""}
 </span>
 </h2>

 <div className="flex gap-2">
 <Button asChild size="sm" variant="outline"><Link to="/dashboard/admin/add-listings" search={{ tab: "single" } as any}>+ Add manually</Link></Button>
 <Button asChild size="sm"><Link to="/dashboard/admin/add-listings" search={{ tab: "bulk" } as any}><Sparkles className="h-3.5 w-3.5" /> Bulk import (CSV)</Link></Button>
 </div>
 </div>

 <div className="card-surface-flat p-3 flex flex-wrap gap-2 items-center mb-3">
 <div className="relative flex-1 min-w-[200px]">
 <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
 <Input placeholder="Search practice, name, specialty, city, email…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 h-9" />
 </div>
 <div className="flex flex-wrap gap-1">
 {STATUS_OPTIONS.map((s) => (
 <button key={s} onClick={() => setStatus(s)}
 className={`text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-md border ${status === s ? "border-primary text-primary bg-primary/5" : "border-border text-muted-foreground hover:text-foreground"}`}>
 {s.replace("_", " ")}
 </button>
 ))}
 </div>
 </div>

 {selected.size > 0 && (
 <div className="rounded-md border border-primary/30 bg-primary/5 p-3 flex flex-wrap items-center gap-2 mb-3">
 <span className="text-sm font-medium">{selected.size} selected</span>
 <div className="flex flex-wrap gap-2 ml-auto">
 <Button size="sm" variant="outline" disabled={busy} onClick={setPremiumBulk}><Crown className="h-3.5 w-3.5" /> Make Premium</Button>
 <Button size="sm" variant="outline" disabled={busy} onClick={() => update({ is_featured: true }, "Featured on")}><Star className="h-3.5 w-3.5" /> Feature</Button>
 <Button size="sm" variant="outline" disabled={busy} onClick={() => update({ is_featured: false }, "Featured off")}>Unfeature</Button>
 <Button size="sm" variant="outline" disabled={busy} onClick={() => update({ listing_status: "claimed_free", plan: "free" }, "Reset to free")}>Set Free</Button>
 <Button size="sm" variant="outline" disabled={busy} onClick={() => update({ is_active: true, moderation_status: "approved" }, "Published")}>Publish</Button>
 <Button size="sm" variant="outline" disabled={busy} onClick={() => update({ is_active: false }, "Unpublished")}>Unpublish</Button>
 <Button size="sm" variant="outline" disabled={busy} onClick={bulkDelete}><Trash2 className="h-3.5 w-3.5" /> Delete</Button>
 </div>
 </div>
 )}

 <div className="card-surface-flat divide-y divide-border">
 <div className="p-3 flex items-center gap-3 bg-muted/30 text-xs uppercase tracking-wider text-muted-foreground">
 <Checkbox checked={allSelected} onCheckedChange={toggleAll} />
 <span>Select all on this page</span>
 {loading && <Loader2 className="h-3.5 w-3.5 animate-spin ml-2" />}
 </div>
 {rows.map((p) => (
 <div key={p.id} className="p-4 flex flex-wrap items-center justify-between gap-3 hover:bg-accent/40 transition">
 <div className="flex items-start gap-3 min-w-0 flex-1">
 <Checkbox checked={selected.has(p.id)} onCheckedChange={() => toggleOne(p.id)} className="mt-1" />
 <Link to="/dashboard/admin/listings/$id" params={{ id: p.id }} className="min-w-0 flex-1">
 <div className="flex items-center gap-2 flex-wrap">
 <p className="font-medium text-sm hover:underline">{p.practice}</p>
 <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-md border border-border text-muted-foreground">{p.listing_status}</span>
 {p.is_featured && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-md border border-amber-500/40 text-amber-600 inline-flex items-center gap-1"><Star className="h-3 w-3" />Featured</span>}
 {!p.is_active && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-md border border-destructive/40 text-destructive">Hidden</span>}
 </div>
 <p className="text-xs text-muted-foreground mt-0.5">{p.specialty} · {p.city}{p.state ? `, ${p.state}` : ""} · {p.views} views</p>
 </Link>
 </div>
 <div className="flex flex-wrap gap-2">
 <Button size="sm" variant={p.listing_status === "premium" ? "default" : "outline"} onClick={() => togglePremium(p)}>
 <Crown className="h-3 w-3" /> {p.listing_status === "premium" ? "Premium" : "Make Premium"}
 </Button>
 <Button size="sm" variant={p.is_featured ? "default" : "outline"} onClick={() => toggleFeatured(p)}>
 <Star className="h-3 w-3" /> {p.is_featured ? "Featured" : "Feature"}
 </Button>
 <Button size="sm" variant="outline" asChild><Link to="/dashboard/admin/listings/$id" params={{ id: p.id }}>Edit</Link></Button>
 <Button size="sm" variant="outline" asChild><Link to="/providers/$id" params={{ id: p.id }}>View <ExternalLink className="h-3 w-3" /></Link></Button>
 <Button size="sm" variant="outline" onClick={() => deleteOne(p.id)}><Trash2 className="h-3 w-3" /></Button>
 </div>
 </div>
 ))}
 {!loading && rows.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">No listings match your filters.</div>}
 </div>

 <div className="flex items-center justify-between mt-4 text-sm">
 <span className="text-muted-foreground">Page {page + 1}</span>
 <div className="flex gap-2">
 <Button size="sm" variant="outline" disabled={page === 0 || loading} onClick={() => setPage((p) => Math.max(0, p - 1))}>Previous</Button>
 <Button size="sm" variant="outline" disabled={!hasMore || loading} onClick={() => setPage((p) => p + 1)}>Next</Button>
 </div>
 </div>

 </section>
 );
}
